/**
 * SubscriptionSuccessPage - Post-Stripe checkout company setup
 *
 * This is the first touchpoint after a customer pays via Stripe checkout.
 * Flow:
 * 1. Customer completes Stripe payment on marketing site
 * 2. Stripe redirects to /subscription/success?session_id=cs_xxx
 * 3. This page shows order confirmation
 * 4. Customer fills company setup form
 * 5. On submit: creates company, user account, logs them in
 * 6. Redirects to /activation for Email Scout data import
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../lib/firebase';
import { LEGAL_URLS } from '../../config/legalUrls';
import {
  CheckCircle,
  Building2,
  Mail,
  Phone,
  User,
  Lock,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  CreditCard,
  LucideProps,
} from 'lucide-react';

// React 19 compatibility
type IconProps = Omit<LucideProps, 'ref'>;
const CheckCircleIcon = CheckCircle as unknown as React.FC<IconProps>;
const Building2Icon = Building2 as unknown as React.FC<IconProps>;
const MailIcon = Mail as unknown as React.FC<IconProps>;
const PhoneIcon = Phone as unknown as React.FC<IconProps>;
const UserIcon = User as unknown as React.FC<IconProps>;
const LockIcon = Lock as unknown as React.FC<IconProps>;
const PlusIcon = Plus as unknown as React.FC<IconProps>;
const Trash2Icon = Trash2 as unknown as React.FC<IconProps>;
const Loader2Icon = Loader2 as unknown as React.FC<IconProps>;
const AlertCircleIcon = AlertCircle as unknown as React.FC<IconProps>;
const EyeIcon = Eye as unknown as React.FC<IconProps>;
const EyeOffIcon = EyeOff as unknown as React.FC<IconProps>;
const CreditCardIcon = CreditCard as unknown as React.FC<IconProps>;

interface SessionDetails {
  email: string;
  tier: string;
  period: string;
  amountTotal: number;
  currency: string;
}

interface Office {
  name: string;
  entityCode: string;
  isHeadOffice: boolean;
}

const TIER_DISPLAY_NAMES: Record<string, string> = {
  plus: 'Valutten Plus',
  pro: 'Valutten Pro',
  enterprise: 'Valutten Enterprise',
};

const PERIOD_DISPLAY_NAMES: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
};

const AGGREGATOR_OPTIONS = [
  { value: 'loan_market', label: 'Loan Market' },
  { value: 'afg', label: 'AFG' },
  { value: 'connective', label: 'Connective' },
  { value: 'finsure', label: 'Finsure' },
  { value: 'plan', label: 'PLAN Australia' },
  { value: 'other', label: 'Other' },
];

const SubscriptionSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  // Loading states
  const [loadingSession, setLoadingSession] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Session data
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Form data
  const [companyName, setCompanyName] = useState('');
  const [aclNumber, setAclNumber] = useState('');
  const [abn, setAbn] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [offices, setOffices] = useState<Office[]>([
    { name: '', entityCode: '', isHeadOffice: true },
  ]);
  const [aggregators, setAggregators] = useState<string[]>([]);
  const [otherAggregatorName, setOtherAggregatorName] = useState('');
  const [commissionEmails, setCommissionEmails] = useState<string[]>([]);
  const [commissionEmailInput, setCommissionEmailInput] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Load session details from Stripe
  useEffect(() => {
    const loadSession = async () => {
      if (!sessionId) {
        setSessionError('No checkout session found. Please complete payment first.');
        setLoadingSession(false);
        return;
      }

      try {
        // Call the function using Firebase SDK (no auth required for this endpoint)
        const getSessionDetails = httpsCallable<{ sessionId: string }, SessionDetails>(
          functions,
          'getCheckoutSessionDetails'
        );
        const result = await getSessionDetails({ sessionId });
        setSessionDetails(result.data);
      } catch (err: any) {
        console.error('[SubscriptionSuccess] Error loading session:', err);
        const errorCode = err.code || err.details?.code;
        if (errorCode === 'functions/already-exists') {
          setSessionError('This checkout session has already been used. Please log in to your account.');
        } else if (errorCode === 'functions/not-found') {
          setSessionError('Invalid or expired checkout session.');
        } else {
          setSessionError(err.message || 'Failed to load checkout details.');
        }
      } finally {
        setLoadingSession(false);
      }
    };

    loadSession();
  }, [sessionId]);

  // Add office
  const addOffice = () => {
    setOffices([...offices, { name: '', entityCode: '', isHeadOffice: false }]);
  };

  // Remove office
  const removeOffice = (index: number) => {
    if (offices.length <= 1) return;
    const newOffices = offices.filter((_, i) => i !== index);
    // Ensure at least one head office
    if (!newOffices.some(o => o.isHeadOffice) && newOffices.length > 0) {
      newOffices[0].isHeadOffice = true;
    }
    setOffices(newOffices);
  };

  // Update office
  const updateOffice = (index: number, field: keyof Office, value: string | boolean) => {
    const newOffices = [...offices];
    if (field === 'isHeadOffice' && value === true) {
      // Only one head office allowed
      newOffices.forEach((o, i) => {
        o.isHeadOffice = i === index;
      });
    } else {
      (newOffices[index] as any)[field] = value;
    }
    setOffices(newOffices);
  };

  // Toggle aggregator
  const toggleAggregator = (value: string) => {
    if (aggregators.includes(value)) {
      setAggregators(aggregators.filter(a => a !== value));
    } else {
      setAggregators([...aggregators, value]);
    }
  };

  // Validate password
  const validatePassword = (pwd: string) => {
    const requirements = {
      length: pwd.length >= 12,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
    };
    return {
      requirements,
      valid: Object.values(requirements).every(Boolean),
    };
  };

  const passwordValidation = validatePassword(password);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validate
    if (!companyName.trim()) {
      setFormError('Company name is required');
      return;
    }
    if (!aclNumber.match(/^\d{4,}$/)) {
      setFormError('ACL must be at least 4 digits');
      return;
    }
    if (!contactName.trim()) {
      setFormError('Contact name is required');
      return;
    }
    if (!passwordValidation.valid) {
      setFormError('Password does not meet requirements');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    if (offices.some(o => !o.name.trim())) {
      setFormError('All offices must have a name');
      return;
    }

    setSubmitting(true);

    try {
      // Define the expected result type
      interface CompleteOnboardingResult {
        success: boolean;
        companyId: string;
        customToken: string;
        message: string;
      }

      // Call the function using Firebase SDK
      const completeOnboarding = httpsCallable<any, CompleteOnboardingResult>(
        functions,
        'completeCheckoutOnboarding'
      );

      const response = await completeOnboarding({
        stripeSessionId: sessionId,
        companyName: companyName.trim(),
        aclNumber: aclNumber.trim(),
        abn: abn.trim() || null,
        contactName: contactName.trim(),
        contactPhone: contactPhone.trim() || null,
        offices: offices.map(o => ({
          name: o.name.trim(),
          entityCode: o.entityCode.trim() || null,
          isHeadOffice: o.isHeadOffice,
        })),
        aggregators: aggregators.length > 0 ? aggregators : [],
        commissionEmails: commissionEmails.length > 0 ? commissionEmails : null,
        otherAggregatorName: otherAggregatorName.trim() || null,
        password,
        // ToS acceptance timestamp (ASIC / Australian Privacy Act compliance)
        tosAcceptedAt: new Date().toISOString(),
      });

      const result = response.data;
      console.log('[SubscriptionSuccess] Company created:', result.companyId);

      // Sign in with the custom token (replaces anonymous auth with real user).
      // Custom tokens expire after 1 hour. If the user took too long filling
      // the form, signInWithCustomToken will fail — redirect to login instead.
      try {
        await signInWithCustomToken(auth, result.customToken);
        console.log('[SubscriptionSuccess] Signed in successfully');
      } catch (signInErr: any) {
        console.error('[SubscriptionSuccess] Custom token sign-in failed:', signInErr);
        // Token expired or invalid — account was already created, redirect to login
        navigate('/login?setup=complete', { replace: true });
        return;
      }

      // Navigate to activation page for Email Scout
      navigate('/activation', { replace: true });
    } catch (err: any) {
      console.error('[SubscriptionSuccess] Error:', err);
      const errorCode = err.code || err.details?.code;
      const errorDetails = err.details;
      let errorMessage: string;
      if (errorCode === 'functions/already-exists') {
        // Server messages on this code are already generic (no email or ACL echoed):
        // lock collision, or an account already tied to this session. Prefer them.
        errorMessage = err.message || 'This checkout session has already been used. Please log in to your existing account.';
      } else if (errorCode === 'functions/internal' && errorDetails?.companyId) {
        // Token creation failed but account was created — redirect to login
        console.log('[SubscriptionSuccess] Account created but token failed, redirecting to login');
        navigate('/login?setup=complete', { replace: true });
        return;
      } else if (errorCode === 'functions/resource-exhausted') {
        errorMessage = err.message || 'Too many attempts. Please try again later.';
      } else {
        errorMessage = err.message || err.details?.message || 'Failed to create account. Please try again.';
      }
      setFormError(errorMessage);
      setSubmitting(false);
    }
  };

  // Format price
  const formatPrice = (cents: number, currency: string) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  // Loading state
  if (loadingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="text-center">
          <Loader2Icon className="animate-spin text-emerald-500 mx-auto mb-4" size={48} />
          <p className="text-white text-lg">Loading your order details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (sessionError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
        <div className="max-w-md w-full bg-gray-800/50 border border-red-500/30 rounded-2xl p-8 text-center">
          <AlertCircleIcon className="text-red-400 mx-auto mb-4" size={48} />
          <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
          <p className="text-gray-400 mb-6">{sessionError}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Order Confirmation Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
            <CheckCircleIcon size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Payment Successful!</h1>
          <p className="text-gray-400">Complete your account setup to get started.</p>
        </div>

        {/* Order Summary */}
        {sessionDetails && (
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <CreditCardIcon size={20} className="text-emerald-400" />
              <h2 className="text-lg font-semibold text-white">Order Summary</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-400">Plan</p>
                <p className="text-white font-medium">
                  {TIER_DISPLAY_NAMES[sessionDetails.tier] || sessionDetails.tier}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Billing</p>
                <p className="text-white font-medium">
                  {PERIOD_DISPLAY_NAMES[sessionDetails.period] || sessionDetails.period}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Amount</p>
                <p className="text-white font-medium">
                  {formatPrice(sessionDetails.amountTotal, sessionDetails.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Email</p>
                <p className="text-white font-medium truncate">{sessionDetails.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Setup Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Information */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2Icon size={20} className="text-indigo-400" />
              <h2 className="text-lg font-semibold text-white">Company Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Smith Mortgage Group"
                  className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  ACL Number *
                </label>
                <input
                  type="text"
                  value={aclNumber}
                  onChange={(e) => setAclNumber(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                  disabled={submitting}
                />
                <p className="text-xs text-gray-500 mt-1">Australian Credit Licence number</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  ABN (Optional)
                </label>
                <input
                  type="text"
                  value={abn}
                  onChange={(e) => setAbn(e.target.value.replace(/\D/g, ''))}
                  placeholder="12 345 678 901"
                  className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  disabled={submitting}
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <UserIcon size={20} className="text-purple-400" />
              <h2 className="text-lg font-semibold text-white">Your Details</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Full Name *
                </label>
                <div className="relative">
                  <UserIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="John Smith"
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                    disabled={submitting}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email
                </label>
                <div className="relative">
                  <MailIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={sessionDetails?.email || ''}
                    disabled
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-700/30 border border-gray-600 rounded-lg text-gray-300 cursor-not-allowed"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Phone (Optional)
                </label>
                <div className="relative">
                  <PhoneIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="0400 000 000"
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Password */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <LockIcon size={20} className="text-yellow-400" />
              <h2 className="text-lg font-semibold text-white">Create Password</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 12 characters"
                    className="w-full px-4 py-2.5 pr-10 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Confirm Password *
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                  disabled={submitting}
                />
              </div>
            </div>

            {/* Password requirements */}
            {password && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  { key: 'length', label: '12+ chars' },
                  { key: 'uppercase', label: 'Uppercase' },
                  { key: 'lowercase', label: 'Lowercase' },
                  { key: 'number', label: 'Number' },
                  { key: 'special', label: 'Special' },
                ].map(({ key, label }) => (
                  <div
                    key={key}
                    className={`text-xs px-2 py-1 rounded text-center ${
                      passwordValidation.requirements[key as keyof typeof passwordValidation.requirements]
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-gray-700/50 text-gray-400'
                    }`}
                  >
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Offices */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Building2Icon size={20} className="text-blue-400" />
                <h2 className="text-lg font-semibold text-white">Offices</h2>
              </div>
              <button
                type="button"
                onClick={addOffice}
                disabled={submitting}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                <PlusIcon size={16} />
                Add Office
              </button>
            </div>

            <div className="space-y-3">
              {offices.map((office, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      type="text"
                      value={office.name}
                      onChange={(e) => updateOffice(index, 'name', e.target.value)}
                      placeholder="Office name"
                      className="px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      disabled={submitting}
                    />
                    <input
                      type="text"
                      value={office.entityCode}
                      onChange={(e) => updateOffice(index, 'entityCode', e.target.value)}
                      placeholder="Entity code (optional)"
                      className="px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      disabled={submitting}
                    />
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={office.isHeadOffice}
                        onChange={(e) => updateOffice(index, 'isHeadOffice', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-emerald-500 focus:ring-emerald-500"
                        disabled={submitting}
                      />
                      Head Office
                    </label>
                  </div>
                  {offices.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeOffice(index)}
                      disabled={submitting}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <Trash2Icon size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Aggregators */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Which aggregators do you use?</h2>
            <p className="text-sm text-gray-400 mb-4">
              Select all that apply. This helps us pre-configure your templates.
            </p>
            <div className="flex flex-wrap gap-2">
              {AGGREGATOR_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleAggregator(option.value)}
                  disabled={submitting}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    aggregators.includes(option.value)
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  } disabled:opacity-50`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Other Aggregator Name input — shown when "Other" is selected */}
            {aggregators.includes('other') && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Aggregator Name
                </label>
                <input
                  type="text"
                  value={otherAggregatorName}
                  onChange={(e) => setOtherAggregatorName(e.target.value)}
                  placeholder="e.g., National Mortgage Brokers"
                  className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  disabled={submitting}
                />
                <p className="text-xs text-gray-500 mt-1">
                  We will build a custom template for your aggregator.
                </p>
              </div>
            )}
          </div>

          {/* Commission Email Addresses */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <MailIcon size={20} className="text-cyan-400" />
              <h2 className="text-lg font-semibold text-white">Commission Email Addresses</h2>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Enter the email addresses that send your commission files (e.g., commissions@loanmarket.com.au).
              This helps us automatically find your files during setup.
            </p>

            {/* Added emails */}
            {commissionEmails.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {commissionEmails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1.5 bg-cyan-500/15 text-cyan-300 text-sm px-3 py-1 rounded-full"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => setCommissionEmails(prev => prev.filter(e => e !== email))}
                      disabled={submitting}
                      className="text-cyan-400 hover:text-white transition-colors"
                    >
                      <Trash2Icon size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add email input */}
            <div className="flex gap-2">
              <input
                type="email"
                value={commissionEmailInput}
                onChange={(e) => setCommissionEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const trimmed = commissionEmailInput.trim().toLowerCase();
                    if (trimmed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) && !commissionEmails.includes(trimmed)) {
                      setCommissionEmails(prev => [...prev, trimmed]);
                      setCommissionEmailInput('');
                    }
                  }
                }}
                placeholder="commissions@example.com.au"
                className="flex-1 px-4 py-2.5 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => {
                  const trimmed = commissionEmailInput.trim().toLowerCase();
                  if (trimmed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) && !commissionEmails.includes(trimmed)) {
                    setCommissionEmails(prev => [...prev, trimmed]);
                    setCommissionEmailInput('');
                  }
                }}
                disabled={submitting}
                className="px-4 py-2.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 hover:text-white transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                <PlusIcon size={16} />
                Add
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Optional. You can also add these later during the import step.
            </p>
          </div>

          {/* Error Message */}
          {formError && (
            <div className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg flex items-start gap-2">
              <AlertCircleIcon size={20} className="flex-shrink-0 mt-0.5" />
              <span>{formError}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting || !passwordValidation.valid || password !== confirmPassword}
            className="w-full py-4 bg-gradient-to-r from-emerald-600 to-green-600 text-white text-lg font-semibold rounded-xl hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2Icon className="animate-spin" size={24} />
                Creating your account...
              </>
            ) : (
              'Complete Setup & Continue'
            )}
          </button>

          <p className="text-center text-sm text-gray-400">
            By continuing, you agree to our{' '}
            <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline">
              Privacy Policy
            </a>.
          </p>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
